// Assistente AI pubblico — widget flottante (launcher + pannello chat).
// Design ispirato ad Airbnb: griglia 8px, angoli morbidi (16px), tipografia
// con titoli forti; i pulsanti d'azione usano i colori attuali (primary).
import React, { useEffect, useRef, useState } from 'react';
import { Bot, X, Send, Sparkles, Loader2, ThumbsUp, ThumbsDown } from 'lucide-react';
import { assistantService } from '@/services/assistant.service';

// Orb animato stile "Siri" — blob colorati che ruotano/pulsano (solo transform/opacity).
function Orb({ className = '', thinking = false }) {
  return (
    <span className={`sila-orb ${thinking ? 'is-thinking' : ''} ${className}`} aria-hidden="true">
      <span className="sila-orb-core" />
      <span className="sila-orb-spin">
        <span className="sila-orb-blob b1" />
        <span className="sila-orb-blob b2" />
        <span className="sila-orb-blob b3" />
      </span>
      <span className="sila-orb-gloss" />
      <span className="sila-orb-ring" />
    </span>
  );
}

function Bubble({ role, text, serverIndex, feedback, onFeedback }) {
  const isUser = role === 'user';
  const showFb = !isUser && typeof serverIndex === 'number';
  return (
    <div className={`flex flex-col ${isUser ? 'items-end' : 'items-start'}`}>
      <div
        className={`max-w-[85%] whitespace-pre-wrap rounded-2xl px-4 py-3 text-sm leading-relaxed shadow-sm ${
          isUser
            ? 'rounded-br-md bg-primary text-primary-foreground'
            : 'rounded-bl-md bg-muted text-foreground'
        }`}
        data-testid={`assistant-msg-${role}`}
      >
        {text}
      </div>
      {showFb && (
        <div className="mt-1.5 flex items-center gap-1 pl-1">
          <button
            onClick={() => onFeedback('up')}
            aria-label="Risposta utile"
            data-testid="assistant-fb-up"
            className={`rounded-full p-1.5 transition-colors ${feedback === 'up' ? 'bg-primary/10 text-primary' : 'text-muted-foreground hover:bg-muted hover:text-foreground'}`}
          >
            <ThumbsUp className="h-3.5 w-3.5" />
          </button>
          <button
            onClick={() => onFeedback('down')}
            aria-label="Risposta non utile"
            data-testid="assistant-fb-down"
            className={`rounded-full p-1.5 transition-colors ${feedback === 'down' ? 'bg-destructive/10 text-destructive' : 'text-muted-foreground hover:bg-muted hover:text-foreground'}`}
          >
            <ThumbsDown className="h-3.5 w-3.5" />
          </button>
        </div>
      )}
    </div>
  );
}

export function AssistantWidget() {
  const [cfg, setCfg] = useState(null);
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const scrollRef = useRef(null);

  useEffect(() => {
    let alive = true;
    assistantService.publicConfig().then((c) => {
      if (!alive) return;
      setCfg(c);
      if (c?.welcome) setMessages([{ role: 'assistant', text: c.welcome }]);
    }).catch(() => {});
    return () => { alive = false; };
  }, []);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, loading, open]);

  if (!cfg || !cfg.enabled) return null;

  const send = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || loading) return;
    const history = messages.map((m) => ({ role: m.role, text: m.text }));
    // indice server della prossima risposta assistant: (turni_reali_precedenti)*2 + 1
    const priorTurns = messages.filter((m) => m.role === 'assistant' && typeof m.serverIndex === 'number').length;
    const serverIndex = priorTurns * 2 + 1;
    setMessages((m) => [...m, { role: 'user', text: msg }]);
    setInput('');
    setLoading(true);
    try {
      const res = await assistantService.chat({ message: msg, history, session_id: sessionId });
      if (res?.session_id) setSessionId(res.session_id);
      setMessages((m) => [...m, { role: 'assistant', text: res?.reply || 'Mi dispiace, riprova.', serverIndex }]);
    } catch {
      setMessages((m) => [...m, { role: 'assistant', text: 'Ops, si è verificato un problema. Riprova tra poco o scrivici dalla pagina Contatti.' }]);
    } finally {
      setLoading(false);
    }
  };

  const sendFeedback = async (msgIdx, serverIndex, rating) => {
    if (!sessionId || typeof serverIndex !== 'number') return;
    setMessages((m) => m.map((x, i) => (i === msgIdx ? { ...x, feedback: rating } : x)));
    try {
      await assistantService.feedback({ session_id: sessionId, rating, message_index: serverIndex });
    } catch { /* best-effort */ }
  };

  const showSuggestions = messages.length <= 1 && !loading && (cfg.suggestions || []).length > 0;

  return (
    <>
      {/* Launcher — orb animato stile Siri */}
      {!open && (
        <button
          onClick={() => setOpen(true)}
          aria-label={`Apri assistente ${cfg.name}`}
          data-testid="assistant-launcher"
          className="group fixed bottom-6 right-6 z-[190] flex items-center gap-2 focus-visible:outline-none"
        >
          <span className="pointer-events-none hidden translate-x-2 rounded-full border border-border bg-card px-3.5 py-2 text-sm font-semibold text-foreground opacity-0 shadow-lg transition-all duration-200 group-hover:translate-x-0 group-hover:opacity-100 sm:block">
            Chiedi a {cfg.name}
          </span>
          <span className="inline-block h-14 w-14 rounded-full shadow-xl transition-transform duration-200 group-hover:-translate-y-0.5 group-focus-visible:ring-2 group-focus-visible:ring-ring group-focus-visible:ring-offset-2">
            <Orb className="h-14 w-14" />
          </span>
        </button>
      )}

      {/* Pannello chat */}
      {open && (
        <div
          className="fixed bottom-4 right-4 z-[190] flex h-[70vh] max-h-[620px] w-[calc(100vw-2rem)] max-w-[400px] flex-col overflow-hidden rounded-3xl border border-border bg-card shadow-2xl duration-200 animate-in fade-in slide-in-from-bottom-4 sm:bottom-6 sm:right-6"
          role="dialog"
          aria-label={`Assistente ${cfg.name}`}
          data-testid="assistant-panel"
        >
          {/* Header */}
          <div className="flex items-center gap-3 border-b border-border px-5 py-4">
            <Orb className="h-11 w-11" thinking={loading} />
            <div className="flex-1">
              <p className="font-display text-base font-bold leading-tight text-foreground">{cfg.name}</p>
              <p className="flex items-center gap-1.5 text-xs text-muted-foreground">
                <span className="inline-block h-2 w-2 rounded-full bg-emerald-500" />
                {cfg.role_label}
              </p>
            </div>
            <button
              onClick={() => setOpen(false)}
              aria-label="Chiudi assistente"
              data-testid="assistant-close"
              className="rounded-full p-2 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Messaggi */}
          <div ref={scrollRef} className="flex-1 space-y-4 overflow-y-auto px-5 py-5" data-testid="assistant-messages">
            {messages.map((m, i) => (
              <Bubble
                key={i}
                role={m.role}
                text={m.text}
                serverIndex={m.serverIndex}
                feedback={m.feedback}
                onFeedback={(rating) => sendFeedback(i, m.serverIndex, rating)}
              />
            ))}
            {loading && (
              <div className="flex justify-start">
                <div className="flex items-center gap-2 rounded-2xl rounded-bl-md bg-muted px-4 py-3 text-sm text-muted-foreground">
                  <Loader2 className="h-4 w-4 animate-spin" /> Sto scrivendo…
                </div>
              </div>
            )}
            {showSuggestions && (
              <div className="flex flex-wrap gap-2 pt-1">
                {cfg.suggestions.map((s, i) => (
                  <button
                    key={i}
                    onClick={() => send(s)}
                    data-testid={`assistant-suggestion-${i}`}
                    className="rounded-full border border-border bg-background px-3.5 py-2 text-left text-sm text-foreground transition-colors hover:border-primary hover:bg-primary/5"
                  >
                    {s}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Input */}
          <form
            onSubmit={(e) => { e.preventDefault(); send(); }}
            className="flex items-center gap-2 border-t border-border p-3"
          >
            <input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder={`Scrivi a ${cfg.name}…`}
              aria-label="Messaggio"
              data-testid="assistant-input"
              className="h-12 flex-1 rounded-2xl border border-border bg-background px-4 text-sm text-foreground outline-none transition-colors placeholder:text-muted-foreground focus:border-primary focus:ring-2 focus:ring-primary/20"
            />
            <button
              type="submit"
              disabled={!input.trim() || loading}
              aria-label="Invia"
              data-testid="assistant-send"
              className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-primary text-primary-foreground transition-transform duration-200 hover:-translate-y-0.5 disabled:cursor-not-allowed disabled:opacity-50"
            >
              <Send className="h-5 w-5" />
            </button>
          </form>
        </div>
      )}
    </>
  );
}

export default AssistantWidget;
